import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { useShell } from '../../shell/ShellContext';
import { ShellRail, useShellUI } from '../../shell/AppShell';
import { leagueMeta, mergeLeagueRollovers, formatTeamRecord } from '../../shell/leagueUtils';
import LinkSleeperBlock from '../../shell/LinkSleeperBlock';
import { tradePackagesAPI, rateMyTradeAPI } from '../../utils/api';
import { ChevronRightIcon } from '../../shell/icons';
import { SleeperAvatar } from '../../shell/SleeperAvatar';

/**
 * TradeBuilderHome — the in-shell /trade-packages surface: your sent/received
 * packages with real grades, plus the flow entry (league → partner → canvas).
 * The canvas itself is the legacy full-page surface until its own rebuild
 * (tracked in the plan — it is a 4.9k-line KEEP-LOGIC component).
 * Public: anonymous visitors get the username search (magic-link send flow
 * still completes on the legacy canvas).
 */

const asArray = (data, ...keys) => {
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const STATUS_LABEL = {
  pending: ['Awaiting grades', 'ft-chip outline'],
  graded: ['Graded', 'ft-chip green'],
  'counter-proposed': ['Countered', 'ft-chip'],
  archived: ['Archived', 'ft-chip outline'],
  draft: ['Draft', 'ft-chip outline'],
};

/* "Herbert + 2027 1st for Justin Jefferson" — first option's pieces, names only. */
const previewLine = (preview) => {
  const names = (items) => (items || []).map((i) => i?.name).filter(Boolean).join(' + ');
  const creator = names(preview?.creatorItems);
  const recipient = names(preview?.recipientItems);
  if (creator && recipient) return `${creator} for ${recipient}`;
  return creator || recipient || null;
};

const TradeBuilderHome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { sleeperUser, leagues, leaguesLoading } = useShell();
  const { toast } = useShellUI();

  const [stage, setStage] = useState('home'); // home | league | partner
  const [packages, setPackages] = useState(null);
  const [league, setLeague] = useState(null);
  const [teams, setTeams] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searched, setSearched] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    if (!user) { setPackages([]); return undefined; }
    let active = true;
    tradePackagesAPI
      .list()
      .then((res) => { if (active) setPackages(asArray(res.data, 'packages', 'tradePackages')); })
      .catch(() => { if (active) setPackages([]); });
    return () => { active = false; };
  }, [user]);

  useEffect(() => {
    if (!league) return undefined;
    let active = true;
    setTeams(null);
    rateMyTradeAPI
      .getLeagueRosters(league.leagueId)
      .then((res) => { if (active) setTeams(res.data?.teams || []); })
      .catch(() => { if (active) setTeams([]); });
    return () => { active = false; };
  }, [league]);

  const identity = searched?.user || sleeperUser;
  const flowLeagues = searched?.leagues || leagues;

  const runSearch = async (e) => {
    if (e) e.preventDefault();
    const name = searchQuery.trim().replace(/^@/, '');
    if (!name) return;
    setSearchLoading(true);
    try {
      const res = await rateMyTradeAPI.getUserLeagues(name);
      setSearched({ user: res.data.user, leagues: mergeLeagueRollovers(res.data.leagues || []) });
      setStage('league');
    } catch {
      toast('No Sleeper user by that name');
    } finally {
      setSearchLoading(false);
    }
  };

  const openCanvas = (team) => {
    if (!identity?.username) {
      toast('Link a Sleeper account first');
      return;
    }
    const params = new URLSearchParams();
    if (league.name) params.set('name', league.name);
    navigate(`/trade-packages/canvas/${identity.username.toLowerCase()}/${league.leagueId}/${team.rosterId}?${params.toString()}`);
  };

  const myTeamUserId = identity?.userId;

  return (
    <div className="ft-view">
      <Helmet>
        <title>Trade Builder — Fantasy Taco</title>
        <meta name="description" content="Build multi-option trade packages and send them to your league mates. Real grades, real answers, faster yes." />
      </Helmet>

      <ShellRail>
        {stage === 'home' ? (
          <>
            {user && packages && packages.length > 0 && (
              <div className="ft-railsec">
                <span className="ft-k">Package status</span>
                <div className="ft-progressrow">
                  <span>Awaiting grades</span>
                  <span className="ft-chip outline">{packages.filter((p) => p.status === 'pending').length}</span>
                </div>
                <div className="ft-progressrow">
                  <span>Graded</span>
                  <span className="ft-chip green">{packages.filter((p) => p.status === 'graded').length}</span>
                </div>
              </div>
            )}
            <div className="ft-railsec">
              <span className="ft-k">Why packages</span>
              <p className="ft-sub" style={{ fontSize: 13.5, marginTop: 8 }}>
                Send up to four options at once. Your league mate grades each one — you learn what actually moves them.
              </p>
            </div>
          </>
        ) : (
          <div className="ft-railsec">
            <span className="ft-k">{league ? league.name : 'Pick a league'}</span>
            {league ? (
              <div className="ft-card" style={{ marginTop: 10 }}>
                <div style={{ fontSize: 12.5, color: 'var(--ft-ink-3)' }}>{leagueMeta(league)} · {league.season}</div>
              </div>
            ) : (
              <p className="ft-sub" style={{ fontSize: 13.5, marginTop: 8 }}>
                The canvas opens with both rosters and live values once you pick a partner.
              </p>
            )}
          </div>
        )}
      </ShellRail>

      <span className="ft-k brand">Trade Builder</span>

      {stage === 'home' && (
        <>
          <h1 className="ft-hero">Build better trades.</h1>
          <p className="ft-sub">Package a few versions of the deal and send your league mate one link. They grade every option — you learn what they'd actually take.</p>

          {!user ? (
            <div className="ft-section" style={{ maxWidth: 480 }}>
              <span className="ft-k">Start with your Sleeper username</span>
              <form onSubmit={runSearch} style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Sleeper username"
                  style={{ flex: 1, background: 'var(--ft-surface)', border: 'none', borderRadius: 'var(--ft-r-input)', padding: '13px 16px', fontSize: 15, outline: 'none' }}
                />
                <button type="submit" className="ft-btn sm" disabled={searchLoading}>
                  {searchLoading ? 'Looking…' : 'Build a package'}
                </button>
              </form>
            </div>
          ) : (
            <>
              <div className="ft-section">
                <div className="ft-qa">
                  <button className="ft-btn sm" onClick={() => setStage('league')}>Start a new package</button>
                </div>
              </div>
              <div className="ft-section">
                <span className="ft-k">Your packages</span>
                {packages === null ? (
                  <div className="ft-loading">Pulling your packages…</div>
                ) : packages.length === 0 ? (
                  <p className="ft-sub" style={{ marginTop: 14 }}>None yet. The first one takes about a minute.</p>
                ) : (
                  <div style={{ marginTop: 12 }}>
                    {packages.map((p) => {
                      const [label, chipClass] = STATUS_LABEL[p.status] || [p.status, 'ft-chip outline'];
                      const received = p.role === 'received' || p.role === 'recipient';
                      const who = received ? p.creatorDisplayName : p.recipientDisplayName;
                      const leagueName = typeof p.league === 'string' ? p.league : p.league?.name || p.league?.leagueName;
                      const peek = previewLine(p.preview);
                      return (
                        <button
                          key={p.id || p.uniqueLink}
                          className="ft-card"
                          style={{ display: 'block', width: '100%', maxWidth: 680, textAlign: 'left', marginBottom: 12, cursor: 'pointer' }}
                          onClick={() => navigate(`/trade-packages/${p.uniqueLink}`)}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <SleeperAvatar url={p.league?.avatarUrl} name={leagueName || who} size={38} radius="10px" />
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: 14.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {received ? `From ${who || 'a league mate'}` : `To ${who || 'a league mate'}`}
                              </div>
                              {peek ? (
                                <div style={{ fontSize: 12.5, color: 'var(--ft-ink-2)', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {peek}
                                </div>
                              ) : null}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7, flexWrap: 'wrap' }}>
                                {leagueName ? <span className="ft-chip">{leagueName}</span> : null}
                                <span style={{ fontSize: 12, color: 'var(--ft-ink-3)' }}>
                                  {[
                                    p.tradesCount ? `${p.tradesCount} option${p.tradesCount === 1 ? '' : 's'}` : null,
                                    fmtDate(p.createdAt),
                                  ].filter(Boolean).join(' · ')}
                                </span>
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
                              {p.avgGrade != null ? <span style={{ fontSize: 13.5, fontWeight: 600 }}>{p.avgGrade} avg</span> : null}
                              <span className={chipClass}>{label}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {stage === 'league' && (
        <>
          <h1 className="ft-hero">Pick the league.</h1>
          <div className="ft-section">
            {searched ? (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
                <span className="ft-k">Leagues for {searched.user?.username}</span>
                <button className="ft-fpill" onClick={() => setSearched(null)}>Clear</button>
              </div>
            ) : null}
            {(!user && !searched) ? (
              <p className="ft-sub">Search your username first.</p>
            ) : (user && !searched && !sleeperUser?.username) ? (
              <LinkSleeperBlock note="Your account has no Sleeper username yet. Link it and your leagues appear here." />
            ) : leaguesLoading && !searched ? (
              <div className="ft-loading">Loading your leagues…</div>
            ) : (
              <div className="ft-rowcards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                {flowLeagues.map((l) => (
                  <button key={l.leagueId} className="ft-leaguecard" onClick={() => { setLeague(l); setStage('partner'); }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <SleeperAvatar id={l.avatar} name={l.name} size={36} radius="10px" />
                      <div style={{ minWidth: 0 }}>
                        <div className="nm" style={{ marginBottom: 1 }}>{l.name}</div>
                        <div className="tm" style={{ marginBottom: 0 }}>{leagueMeta(l)} · {l.season}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button className="ft-btn sm ghost" style={{ marginTop: 20 }} onClick={() => setStage('home')}>Back</button>
        </>
      )}

      {stage === 'partner' && league && (
        <>
          <h1 className="ft-hero">Pick your trade partner.</h1>
          <p className="ft-sub">{league.name} — the canvas opens with both rosters and live values.</p>
          <div className="ft-section">
            {!teams ? (
              <div className="ft-loading">Pulling the league from Sleeper…</div>
            ) : (
              <div className="ft-rowcards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
                {teams
                  .filter((t) => !myTeamUserId || t.sleeperUserId !== myTeamUserId)
                  .map((t) => {
                    const prevLabel = league.season && !Number.isNaN(Number(league.season))
                      ? String(Number(league.season) - 1)
                      : null;
                    const record = formatTeamRecord(t.record, t.prevRecord, prevLabel);
                    return (
                      <button key={t.rosterId} className="ft-leaguecard" onClick={() => openCanvas(t)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <SleeperAvatar id={t.avatar} name={t.teamName || t.ownerName} size={32} />
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div className="nm" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.teamName}</div>
                            <div className="tm" style={{ marginBottom: 0 }}>
                              {t.ownerName}
                              {record ? ` · ${record}` : ''}
                            </div>
                          </div>
                          <ChevronRightIcon style={{ width: 14, height: 14, color: 'var(--ft-ink-3)', flexShrink: 0 }} />
                        </div>
                      </button>
                    );
                  })}
              </div>
            )}
          </div>
          <button className="ft-btn sm ghost" style={{ marginTop: 20 }} onClick={() => { setLeague(null); setStage('league'); }}>Back</button>
        </>
      )}
    </div>
  );
};

export default TradeBuilderHome;
